import axios from "axios";

import { API_BASE_URL } from "../config/api";

const axiosInstance = axios.create({
  baseURL: API_BASE_URL,
  withCredentials: true,
  headers: {
    "Content-Type": "application/json",
  },
});

export function fetchGuestRecommendations(guestProfile) {
  if (!guestProfile?.guest_id) {
    throw new Error("Canonical guest identity is required");
  }

  return axiosInstance.post("/guest-recommendations", guestProfile);
}

export function fetchMyProfile() {
  return axiosInstance.get("/me/profile");
}

export function saveMyProfile(profile) {
  return axiosInstance.put("/me/profile", profile);
}

export function fetchSavedShows() {
  return axiosInstance.get("/me/saved-shows");
}

export function saveShow(showId) {
  return axiosInstance.post(`/me/saved-shows/${showId}`);
}

export function removeSavedShow(showId) {
  return axiosInstance.delete(`/me/saved-shows/${showId}`);
}

export function fetchRecommendationHistory() {
  return axiosInstance.get("/me/recommendation-history");
}

export function recordRecommendationHistory(session) {
  return axiosInstance.post("/me/recommendation-history", session);
}

export function convertGuestProfile({ guest_id, profile, results = [] }) {
  if (!guest_id || !profile) {
    throw new Error("Guest conversion state is required");
  }

  return axiosInstance.post("/me/convert-guest", {
    guest_id,
    profile: {
      age: profile.age,
      favorite_genres: profile.favorite_genres || [],
      favorite_moods: profile.favorite_moods || [],
      commitment_pref: profile.commitment_pref || null,
      commitment_flexible: profile.commitment_flexible === true,
      vibe_pref: profile.vibe_pref || null,
      laugh_pref: profile.laugh_pref || null,
      blocked_content: profile.blocked_content || [],
      cultural_tags: profile.cultural_tags || [],
      ranking_preference: profile.ranking_preference || null,
      available_platforms: profile.available_platforms || [],
    },
    results: results.map((result) => ({
      show_id: result.show_id ?? result.id ?? null,
      title: result.title ?? null,
    })),
  });
}

export function convertGuestToUser(payload) {
  return convertGuestProfile(payload);
}

export const fetchTrending = (limit = 5, genre, platform) => {
  return axiosInstance.get("/trending", {
    params: { limit, genre, platform },
  });
};

export const fetchAllShows = (filters = {}) => {
  return axiosInstance.get("/all-shows", { params: filters });
};

// Admin Dashboard: Show Management
export const fetchAdminShows = () => {
  return axiosInstance.get("/admin/shows");
};

export const createAdminShow = (show) => {
  return axiosInstance.post("/admin/shows", show);
};

export const updateAdminShow = (showId, updatedShow) => {
  return axiosInstance.put(`/admin/shows/${showId}`, updatedShow);
};

export const deleteAdminShow = (showId) => {
  return axiosInstance.delete(`/admin/shows/${showId}`);
};

export async function submitFeedback({
  guest_id = null,
  user_id = null,
  show_title,
  feedback,
  genres = [],
  moods = [],
  cultural_tags = [],
  laugh_factor = null,
  commitment_level = null,
}) {
  if (!guest_id && !user_id) {
    throw new Error("Feedback identity is required");
  }

  if (guest_id && user_id) {
    throw new Error("Provide only one feedback identity");
  }

  try {
    const response = await axiosInstance.post("/feedback", {
      guest_id,
      user_id,
      show_title,
      feedback,
      genres,
      moods,
      cultural_tags,
      laugh_factor,
      commitment_level,
    });

    return response.data;
  } catch (error) {
    console.error(
      "Feedback submission error:",
      error.response?.data?.detail || error.message
    );
    throw error;
  }
}

export async function fetchFeedback(identityId, show_title, identityType = "guest") {
  if (!identityId) {
    console.error("Feedback identity is required");
    return { feedback: null };
  }

  const params = { show_title };

  if (identityType === "user") {
    params.user_id = identityId;
  } else {
    params.guest_id = identityId;
  }

  try {
    const response = await axiosInstance.get("/feedback", { params });
    return response.data;
  } catch (error) {
    console.error(
      "Error fetching existing feedback:",
      error.response?.data?.detail || error.message
    );
    return { feedback: null };
  }
}

export default axiosInstance;
